import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Wallet, TrendingUp, ClipboardCheck, Bell, ArrowRight, Sparkles } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const spendData = Array.from({ length: 12 }).map((_, i) => ({ m: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i], v: 5000 + Math.round(Math.random() * 4000) + i * 300 }));

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    api.get("/loans").then((r) => setLoans(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    api.get("/transactions").then((r) => setTxns(r.data)).catch(() => {});
  }, []);

  const active = loans.filter((l) => ["approved","disbursed","active"].includes(l.status));
  const totalOutstanding = active.reduce((s, l) => s + Number(l.approved_amount || l.amount || 0), 0);
  const kycProgress = user?.kyc_status === "verified" ? 100 : (user?.profile_completed ? 60 : 20);

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="customer-dashboard">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Overview</div>
            <h1 className="font-display text-3xl mt-2">Hello, {user?.name?.split(" ")[0] || "there"} 👋</h1>
            <p className="text-sm text-muted-foreground mt-1">Here's your money at a glance.</p>
          </div>
          <Link to="/app/apply"><Button className="rounded-full brand-gradient text-white" data-testid="dashboard-apply">Apply for a loan <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <Card className="p-5 card-hover">
            <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground uppercase tracking-widest">Active loans</div><Wallet className="h-4 w-4 text-primary" /></div>
            <div className="font-display text-3xl mt-3">{active.length}</div>
          </Card>
          <Card className="p-5 card-hover">
            <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground uppercase tracking-widest">Outstanding</div><TrendingUp className="h-4 w-4 text-primary" /></div>
            <div className="font-display text-3xl mt-3">₹{totalOutstanding.toLocaleString("en-IN")}</div>
          </Card>
          <Card className="p-5 card-hover">
            <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground uppercase tracking-widest">KYC</div><ClipboardCheck className="h-4 w-4 text-primary" /></div>
            <div className="font-display text-3xl mt-3 capitalize">{user?.kyc_status || "pending"}</div>
            <Progress value={kycProgress} className="mt-3 h-1.5" />
          </Card>
          <Card className="p-5 card-hover">
            <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground uppercase tracking-widest">Notifications</div><Bell className="h-4 w-4 text-primary" /></div>
            <div className="font-display text-3xl mt-3">{notifs.filter((n) => !n.read).length}</div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Repayment trend</div>
                <div className="font-display text-xl">Monthly outflow</div>
              </div>
              <div className="text-xs text-muted-foreground">Illustrative</div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={spendData}>
                  <defs>
                    <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4F46E5" stopOpacity={0.4}/><stop offset="100%" stopColor="#4F46E5" stopOpacity={0}/></linearGradient>
                  </defs>
                  <XAxis dataKey="m" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip formatter={(v) => `₹${v.toLocaleString("en-IN")}`} />
                  <Area type="monotone" dataKey="v" stroke="#4F46E5" fill="url(#grad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl">Quick actions</div>
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div className="space-y-2 text-sm">
              <Link to="/app/apply" className="block rounded-xl border border-border p-3 hover:border-primary/50">Apply for a new loan</Link>
              <Link to="/app/repayment" className="block rounded-xl border border-border p-3 hover:border-primary/50">Pay upcoming EMI</Link>
              <Link to="/app/profile" className="block rounded-xl border border-border p-3 hover:border-primary/50">Complete your KYC</Link>
              <Link to="/app/statements" className="block rounded-xl border border-border p-3 hover:border-primary/50">Download statements</Link>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">Recent applications</div>
              <Link to="/app/loans" className="text-xs text-primary">View all</Link>
            </div>
            {loans.length === 0 ? (
              <div className="text-sm text-muted-foreground">No loan applications yet. <Link to="/app/apply" className="text-primary">Apply now</Link></div>
            ) : (
              <div className="space-y-3">
                {loans.slice(0, 4).map((l) => (
                  <div key={l.id} className="flex items-center justify-between border-b border-border/60 pb-3 last:border-0 last:pb-0">
                    <div>
                      <div className="text-sm font-medium capitalize">{l.loan_type.replace("_"," ")}</div>
                      <div className="text-xs text-muted-foreground">₹{Number(l.amount).toLocaleString("en-IN")} · {l.tenure_months} mo</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${l.status === "approved" ? "bg-emerald-500/10 text-emerald-600" : l.status === "rejected" ? "bg-red-500/10 text-red-600" : "bg-primary/10 text-primary"}`}>{l.status}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">Latest transactions</div>
              <Link to="/app/statements" className="text-xs text-primary">All statements</Link>
            </div>
            {txns.length === 0 ? (
              <div className="text-sm text-muted-foreground">No transactions yet.</div>
            ) : (
              <div className="space-y-3 text-sm">
                {txns.slice(0, 5).map((t) => (
                  <div key={t.id} className="flex justify-between border-b border-border/60 pb-2 last:border-0">
                    <div>
                      <div>{t.method}</div>
                      <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="font-medium">₹{Number(t.amount).toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
